import { useState, useRef } from 'react'
import './styles.scss'
import UnbeatableSection from '../../Components/UnbeatableSection'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { solid, brands } from '@fortawesome/fontawesome-svg-core/import.macro'
import Animate from '../../Components/Animate'
import { useForm } from '../../Hooks/useForm'
import emailjs from '@emailjs/browser'

const serviceId  = process.env.REACT_APP_EMAILJS_SERVICE_ID
const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_CONTACT
const publicId   = process.env.REACT_APP_EMAILJS_PUBLIC_KEY

const SERVICES = [
  'Air Freight',
  'Ocean Freight',
  'Cargo Transportation',
  'Packaging and Storage',
  'General Inquiry',
]

const ContactUs = () => {
  const formRef = useRef(null)
  const [submitted, setSubmitted] = useState(false)

  const { form, onChange, reset } = useForm({
    name: '',
    email: '',
    phone: '',
    subject: '',
    service: '',
    msg: '',
    loading: false,
  })

  const handleChange = (e) => onChange(e.target.name, e.target.value)

  const handleSubmit = async (e) => {
    e.preventDefault()
    onChange('loading', true)
    try {
      await emailjs.send(serviceId, templateId, form, publicId)
      setSubmitted(true)
      reset()
    } catch (err) {
      console.error('EmailJS error', err)
    }
    onChange('loading', false)
  }

  return (
    <div className="contact-us">
      <div className="contact-us__top">
        <h1>Contact Us</h1>
        <p>We're here to help. Reach out and we'll respond within 24 hours.</p>
      </div>

      <div className="contact-us__content">
        <section className="contact-us__main">
          <Animate type="fade-left" className="contact-us__info">
            <span className="contact-us__info__tag">Get In Touch</span>
            <h2>Let's Talk About Your Shipment</h2>
            <p>
              Whether you have questions about our services, need a custom
              quote, or want to track an existing shipment — our team is
              ready to assist you.
            </p>

            <div className="contact-us__info__items">
              <div className="contact-us__info__item">
                <div className="contact-us__info__item__icon">
                  <FontAwesomeIcon icon={solid('phone')} />
                </div>
                <div>
                  <span className="contact-us__info__item__label">Call Us</span>
                  <span className="contact-us__info__item__value">+1 (512) 643-7634</span>
                </div>
              </div>

              <div className="contact-us__info__item">
                <div className="contact-us__info__item__icon">
                  <FontAwesomeIcon icon={solid('envelope')} />
                </div>
                <div>
                  <span className="contact-us__info__item__label">Email Us</span>
                  <span className="contact-us__info__item__value">business@stanbitlogistics.com</span>
                </div>
              </div>

              <div className="contact-us__info__item">
                <div className="contact-us__info__item__icon">
                  <FontAwesomeIcon icon={solid('location-dot')} />
                </div>
                <div>
                  <span className="contact-us__info__item__label">Location</span>
                  <span className="contact-us__info__item__value">Global Facility Office</span>
                </div>
              </div>

              <div className="contact-us__info__item">
                <div className="contact-us__info__item__icon">
                  <FontAwesomeIcon icon={solid('clock')} />
                </div>
                <div>
                  <span className="contact-us__info__item__label">Working Hours</span>
                  <span className="contact-us__info__item__value">Mon – Sat, 8:00 AM – 8:00 PM</span>
                </div>
              </div>
            </div>

            <div className="contact-us__info__socials">
              <button aria-label="Facebook"><FontAwesomeIcon icon={brands('facebook-f')} /></button>
              <button aria-label="LinkedIn"><FontAwesomeIcon icon={brands('linkedin-in')} /></button>
              <button aria-label="Twitter"><FontAwesomeIcon icon={brands('twitter')} /></button>
            </div>
          </Animate>

          <Animate type="fade-right" delay={0.12} className="contact-us__form-wrap">
            {submitted ? (
              <div className="contact-us__success">
                <div className="contact-us__success__icon">
                  <FontAwesomeIcon icon={solid('circle-check')} />
                </div>
                <h3>Message Sent!</h3>
                <p>Thank you for reaching out. We'll get back to you within 24 hours.</p>
                <button onClick={() => setSubmitted(false)}>Send Another Message</button>
              </div>
            ) : (
              <form ref={formRef} className="contact-us__form" onSubmit={handleSubmit} noValidate>
                <h3>Send Us A Message</h3>
                <p className="contact-us__form__sub">Fill in the form below and we'll be in touch shortly.</p>

                <div className="contact-us__form__row">
                  <div className="cf-field">
                    <label htmlFor="cf-name">Full Name <span>*</span></label>
                    <input
                      id="cf-name"
                      name="name"
                      type="text"
                      placeholder="John Doe"
                      required
                      value={form.name}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="cf-field">
                    <label htmlFor="cf-email">Email Address <span>*</span></label>
                    <input
                      id="cf-email"
                      name="email"
                      type="email"
                      placeholder="john@example.com"
                      required
                      value={form.email}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="contact-us__form__row">
                  <div className="cf-field">
                    <label htmlFor="cf-phone">Phone Number</label>
                    <input
                      id="cf-phone"
                      name="phone"
                      type="tel"
                      placeholder="+1 (555) 000-0000"
                      value={form.phone}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="cf-field">
                    <label htmlFor="cf-subject">Subject <span>*</span></label>
                    <input
                      id="cf-subject"
                      name="subject"
                      type="text"
                      placeholder="How can we help?"
                      required
                      value={form.subject}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="cf-field">
                  <label htmlFor="cf-service">Service of Interest</label>
                  <select
                    id="cf-service"
                    name="service"
                    value={form.service}
                    onChange={handleChange}
                  >
                    <option value="">Select a service…</option>
                    {SERVICES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>

                <div className="cf-field">
                  <label htmlFor="cf-msg">Message <span>*</span></label>
                  <textarea
                    id="cf-msg"
                    name="msg"
                    rows={5}
                    placeholder="Tell us more about your shipment or inquiry…"
                    required
                    value={form.msg}
                    onChange={handleChange}
                  />
                </div>

                <button
                  type="submit"
                  className={`contact-us__form__submit${form.loading ? ' loading' : ''}`}
                  disabled={form.loading}
                >
                  {form.loading ? (
                    <>
                      <FontAwesomeIcon icon={solid('spinner')} className="spin" />
                      Sending…
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={solid('paper-plane')} />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            )}
          </Animate>
        </section>

        <UnbeatableSection />
      </div>
    </div>
  )
}

export default ContactUs
